import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { plural } from '@lingui/core/macro';
import { useLingui } from '@lingui/react/macro';

import Pets from '@rover/icons/svg/pets.svg';
import { Box, Flex } from '@rover/kibble/core';
import useTheme from '@rover/kibble/themes/useTheme';
import { petNameListify } from '@rover/react-lib/src/utils/pets';
import type { FrontendConfigurationSettingsDogSizeBuckets } from '@rover/rsdk/src/apiClient/latest/model/frontendConfigurationSettingsDogSizeBuckets';
import { emitAnalyticsEvent } from '@rover/rsdk/src/modules/Analytics';
import useAvailablePets from '@rover/shared/js/hooks/useAvailablePets';
import type { SearchFiltersOptions } from '@rover/types/src/apiResponses/SearchOptionsResponse';
import type { Pet } from '@rover/types/src/Pet';
import { PetType } from '@rover/types/src/Pet';
import type { SearchFilters } from '@rover/types/src/SearchFilters';
import type { ServiceType } from '@rover/types/src/ServiceType';

import focusOnElement from '../../../utilities/focusOnElement';
import TruncatedText from '../../TruncatedText';
import {
  SearchTopFiltersPetsGroupDismiss,
  SearchTopFiltersPetsGroupOpen,
  SearchTopFiltersPetsGroupSave,
} from '../analyticsEvents';

import FilterDropdown from './FilterDropdown';
import FilterSection from './FilterSection';
import PetsDropdownContent from './PetsDropdownContent';

type PetsSectionProps = {
  dogCount?: number;
  catCount?: number;
  puppyCount?: number;
  selectedPets?: Pet[];
  updateFiltersAndFireSearchOnChange: (filters: Partial<SearchFilters>) => void;
  searchFilters: SearchFilters;
  allPets: Pet[];
  petSizeBuckets: FrontendConfigurationSettingsDogSizeBuckets;
  weightUnit: string;
  serviceTypeFilterOptions: SearchFiltersOptions;
  getServiceTypeBySlug: (arg0: string) => ServiceType | null | undefined;
  isDropdownOpen: boolean;
  setIsDropdownOpen: (value: boolean) => void;
};

const PetsSection = ({
  dogCount = 0,
  catCount = 0,
  puppyCount = 0,
  selectedPets = [],
  updateFiltersAndFireSearchOnChange,
  searchFilters,
  allPets,
  petSizeBuckets,
  weightUnit,
  serviceTypeFilterOptions,
  getServiceTypeBySlug,
  isDropdownOpen,
  setIsDropdownOpen,
}: PetsSectionProps): JSX.Element => {
  const { t } = useLingui();
  const [localFilters, setLocalFilters] = useState<Partial<SearchFilters>>({});
  const sectionRef = useRef<HTMLElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const dialogId = useId();
  const theme = useTheme();

  useEffect(() => {
    if (isDropdownOpen) {
      setLocalFilters({
        dogCount: searchFilters.dogCount,
        catCount: searchFilters.catCount,
        puppyCount: searchFilters.puppyCount,
        pet: searchFilters.pet,
        petType: searchFilters.petType,
        smallDogs: searchFilters.smallDogs,
        mediumDogs: searchFilters.mediumDogs,
        largeDogs: searchFilters.largeDogs,
        giantDogs: searchFilters.giantDogs,
      });

      focusOnElement(dropdownRef);
    }
  }, [isDropdownOpen, searchFilters]);

  const availablePets = useAvailablePets(searchFilters.serviceType);

  const petCount = useMemo(() => {
    let count = 0;
    if (availablePets.includes(PetType.DOG)) {
      count += dogCount;
    }
    if (availablePets.includes(PetType.PUPPY)) {
      count += puppyCount;
    }
    if (availablePets.includes(PetType.CAT)) {
      count += catCount;
    }
    return count;
  }, [availablePets, dogCount, catCount, puppyCount]);

  const petCountLabel = useMemo(() => {
    const parts: string[] = [];
    if (availablePets.includes(PetType.DOG) && dogCount > 0) {
      parts.push(
        t({
          message: plural(dogCount, { one: '# dog', other: '# dogs' }),
        })
      );
    }
    if (availablePets.includes(PetType.CAT) && catCount > 0) {
      parts.push(
        t({
          message: plural(catCount, { one: '# cat', other: '# cats' }),
        })
      );
    }
    if (availablePets.includes(PetType.PUPPY) && puppyCount > 0) {
      parts.push(
        t({
          message: plural(puppyCount, { one: '# puppy', other: '# puppies' }),
        })
      );
    }
    return parts.join(', ');
  }, [availablePets, dogCount, catCount, puppyCount, t]);

  const mergedFilters = useMemo(() => {
    return { ...searchFilters, ...localFilters };
  }, [searchFilters, localFilters]);

  const localDogCount = localFilters.dogCount ?? dogCount;
  const localCatCount = localFilters.catCount ?? catCount;
  const localPuppyCount = localFilters.puppyCount ?? puppyCount;

  const handleFiltersChange = useCallback((filters: Partial<SearchFilters>) => {
    setLocalFilters((prev) => ({ ...prev, ...filters }));
  }, []);

  const handleSave = useCallback(() => {
    emitAnalyticsEvent(new SearchTopFiltersPetsGroupSave());
    updateFiltersAndFireSearchOnChange(localFilters);
    setIsDropdownOpen(false);

    focusOnElement(sectionRef);
  }, [localFilters, updateFiltersAndFireSearchOnChange, setIsDropdownOpen]);

  const getTextContent = (): string => {
    if (selectedPets.length > 0) {
      return petNameListify(selectedPets.map((pet) => pet.name));
    }
    if (petCount > 0) {
      return petCountLabel;
    }
    return t`Add pets`;
  };

  const handleSectionClick = (): void => {
    if (!isDropdownOpen) {
      emitAnalyticsEvent(new SearchTopFiltersPetsGroupOpen());
    } else {
      // We should also fire the dismiss event if the popover is closed by clicking
      // the button again.
      emitAnalyticsEvent(new SearchTopFiltersPetsGroupDismiss());
    }
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleClose = (): void => {
    emitAnalyticsEvent(new SearchTopFiltersPetsGroupDismiss());
    setIsDropdownOpen(false);

    focusOnElement(sectionRef);
  };

  return (
    <Box position="relative" flex="1" height="100%">
      <FilterSection onClick={handleSectionClick} ref={sectionRef} isOpen={isDropdownOpen}>
        <Flex alignItems="center" gap="2x" width="100%">
          <Box flexShrink="0">
            <Pets width={24} height={24} fill={theme.colors.brand.active_brand} />
          </Box>
          <TruncatedText
            size="100"
            textColor={selectedPets.length > 0 || petCount > 0 ? 'primary' : 'placeholder'}
            textAlign="left"
          >
            {getTextContent()}
          </TruncatedText>
        </Flex>
      </FilterSection>

      {isDropdownOpen && (
        <FilterDropdown
          ref={dropdownRef}
          onClose={handleClose}
          triggerRef={sectionRef}
          width="600px"
          align="right"
          role="dialog"
          id={dialogId}
          aria-label={t`Pet filters`}
        >
          <PetsDropdownContent
            allPets={allPets}
            searchFilters={mergedFilters}
            dogCount={localDogCount}
            catCount={localCatCount}
            puppyCount={localPuppyCount}
            petSizeBuckets={petSizeBuckets}
            weightUnit={weightUnit}
            onChange={handleFiltersChange}
            onSave={handleSave}
            serviceTypeFilterOptions={serviceTypeFilterOptions}
            getServiceTypeBySlug={getServiceTypeBySlug}
          />
        </FilterDropdown>
      )}
    </Box>
  );
};

export default PetsSection;
